import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import Toggle from './Toggle';

/**
 * MoreFunctions atom — displays a three-dot menu with configurable actions.
 *
 * @param {Array} options
 * Example:
 * [
 *   { label: 'Edit', onClick: () => {} },
 *   { label: 'Delete', onClick: () => {}, danger: true }
 * ]
 */
export default function MoreFunctions({
  options = [],
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option) => {
    option.onClick?.();
    if (!option.keepOpen) setIsOpen(false);
  };

  return (
    <div
      ref={menuRef}
      className={`ca-more-functions ${className}`.trim()}
    >
      <button
        type="button"
        className="ca-more-functions__button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="More options"
        aria-expanded={isOpen}
      >
        <MoreVertical size={16} />
      </button>

      {isOpen && options.length > 0 && (
        <div className="ca-more-functions__menu">
          {options.map((option, index) => (
            option.toggle ? (
              <div
                key={option.key || index}
                className="ca-more-functions__item ca-more-functions__item--toggle"
                onClick={(event) => event.stopPropagation()}
              >
                <span>{option.label}</span>
                <Toggle
                  checked={Boolean(option.toggle.checked)}
                  disabled={Boolean(option.toggle.disabled)}
                  aria-label={option.label}
                  onChange={(nextChecked) => {
                    option.toggle.onChange?.(nextChecked);
                    if (!option.keepOpen) setIsOpen(false);
                  }}
                />
              </div>
            ) : (
              <button
                key={option.key || index}
                type="button"
                className={`ca-more-functions__item ${
                  option.danger ? 'ca-more-functions__item--danger' : ''
                }`}
                onClick={() => handleOptionClick(option)}
              >
                {option.icon && (
                  <span className="ca-more-functions__icon">
                    {option.icon}
                  </span>
                )}

                <span>{option.label}</span>
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}
